<script setup lang="ts">
import {
  PhAndroidLogo,
  PhAppleLogo,
  PhGlobeHemisphereWest,
  PhWechatLogo,
  PhWindowsLogo,
} from '@phosphor-icons/vue'
import { onMounted, ref } from 'vue'
import { getDownloadEntries, getAppDownloadQr, type DownloadEntry } from '@/api/downloads'
import { useWebsiteSettings } from '@/composables/useWebsiteSettings'

const { settings, webUrl } = useWebsiteSettings()
const entries = ref<DownloadEntry[]>([])
const qr = ref<string | null>(null)
const macs = [
  { code: 'MAC_INTEL', name: 'Intel' },
  { code: 'MAC_APPLE_SILICON', name: 'Apple Silicon' },
]
function url(code: string) {
  const item = entries.value.find((item) => item.code === code)
  return item?.available ? item.url : null
}
async function load() {
  try {
    entries.value = await getDownloadEntries()
  } catch {
    entries.value = []
  }
}
onMounted(() => {
  void load()
  void getAppDownloadQr()
    .then((data) => {
      qr.value = data.appDownloadQrCodeUrl
    })
    .catch(() => {
      qr.value = null
    })
})
</script>

<template>
  <section id="download" class="download-section section">
    <div class="container">
      <div class="section-heading centered">
        <h2>六大平台覆盖，随时开始实验</h2>
        <p>点击对应平台立即开始实验</p>
      </div>
      <div class="download-grid">
        <a class="platform-card" :href="webUrl"
          ><PhGlobeHemisphereWest :size="42" weight="duotone" /><strong>网页版</strong
          ><span class="platform-hover-tip">开始实验</span></a
        >
        <component
          :is="url('WINDOWS') ? 'a' : 'div'"
          class="platform-card"
          :href="url('WINDOWS') || undefined"
          tabindex="0"
        >
          <PhWindowsLogo :size="42" weight="duotone" /><strong>Windows</strong>
          <span v-if="url('WINDOWS')" class="platform-hover-tip">立即下载</span>
        </component>
        <div class="platform-card mobile-card" tabindex="0">
          <PhAppleLogo :size="42" weight="duotone" /><strong>macOS</strong>
          <div class="qr-box mac-options">
            <component
              :is="url(item.code) ? 'a' : 'span'"
              v-for="item in macs"
              :key="item.code"
              :href="url(item.code) || undefined"
              class="mac-option"
              :class="{ unavailable: !url(item.code) }"
              >{{ item.name }}</component
            >
          </div>
        </div>
        <template
          v-for="item in [
            { name: 'iOS', icon: PhAppleLogo },
            { name: 'Android', icon: PhAndroidLogo },
          ]"
          :key="item.name"
        >
          <div class="platform-card" :class="{ 'mobile-card is-upcoming': !!qr }" tabindex="0">
            <component :is="item.icon" :size="42" weight="duotone" /><strong>{{ item.name }}</strong
            ><span v-if="qr" class="qr-box"
              ><img :src="qr" :alt="`${item.name} App 下载二维码`" @error="qr = null"
            /></span>
          </div>
        </template>
        <div
          class="platform-card"
          :class="{ 'mobile-card is-upcoming': !!settings.miniProgramQrUrl }"
          tabindex="0"
        >
          <PhWechatLogo :size="42" weight="duotone" /><strong>微信小程序</strong
          ><span v-if="settings.miniProgramQrUrl" class="qr-box"
            ><img :src="settings.miniProgramQrUrl" alt="微信小程序二维码"
          /></span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.download-grid {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}
.qr-box img {
  object-fit: contain;
}
.mobile-card:is(:hover, :focus-within) .mac-options {
  pointer-events: auto;
}
.platform-card:focus-within .platform-hover-tip {
  opacity: 1;
  transform: translate(-50%, 0);
}
.mac-options {
  width: calc(100% - 20px);
  height: auto;
  min-height: 140px;
  gap: 8px;
  padding: 12px;
  box-sizing: border-box;
  flex-direction: column;
}
.mac-option {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  padding: 8px 4px;
  border-radius: 6px;
  background: #edf5ff;
  color: #1469d5;
  text-decoration: none;
  font-size: 13px;
}
.mac-option.unavailable {
  color: #8593a8;
  background: #f5f7fa;
}
@media (min-width: 761px) and (max-width: 1080px) {
  .download-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 760px) {
  .download-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
